import { useCallback, useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  listSegmentVersions, getSegmentVersion, restoreSegmentVersion,
  type SegmentVersionMeta, type SegmentVersionSource,
} from '@/lib/api'
import { normalizeRichTextForEditor } from '@/components/drafting/segmentAssembly'

const SOURCE_LABELS: Record<SegmentVersionSource, string> = {
  manual_save: 'Edit',
  ai_draft: 'AI draft',
  ai_revision: 'AI revision',
  conflict_backup: 'Your unsaved edits (conflict)',
  manual_backup: 'Your unsaved edits (backup)',
  restore: 'Restored version',
}

/**
 * A coalesced manual_save session carries a created→updated span (start → last
 * edit); render it as a range. Point-in-time rows (AI / backup / restore, where
 * updated == created) render as a single timestamp. The session always fits in
 * one day, so the end shows time only.
 */
export function formatVersionTimestamp(createdAt: string, updatedAt: string): string {
  const start = new Date(createdAt)
  if (createdAt === updatedAt) return start.toLocaleString()
  return `${start.toLocaleString()} – ${new Date(updatedAt).toLocaleTimeString()}`
}

interface VersionHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  sectionId: string
  /**
   * Names the section whose history is shown. Only supplied by the composite
   * (full-document) editor, where the dialog scopes to the caret's section and
   * the user would otherwise have no way to tell which one that is.
   */
  sectionHeading?: string | null
  currentUserId: string | undefined
  canWrite: boolean
  onRestored: (result: { version: number; content: string; wordCount: number }) => void
}

export function VersionHistoryDialog({
  open, onOpenChange, sessionId, sectionId, sectionHeading, currentUserId, canWrite, onRestored,
}: VersionHistoryDialogProps) {
  const [versions, setVersions] = useState<SegmentVersionMeta[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [isRestoring, setIsRestoring] = useState(false)

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    setSelectedId(null)
    setPreviewContent(null)
    listSegmentVersions(sessionId, sectionId)
      .then((res) => setVersions(res.versions))
      .catch(() => toast.error('Could not load version history.'))
      .finally(() => setIsLoading(false))
  }, [open, sessionId, sectionId])

  const handleSelect = useCallback(async (versionId: string) => {
    setSelectedId(versionId)
    setPreviewContent(null)
    try {
      const version = await getSegmentVersion(sessionId, sectionId, versionId)
      // Version rows keep whatever format wrote them — the drafter agent writes
      // Markdown, editor saves write HTML. Normalise on the way in so both the
      // preview and the restore below deal only in HTML.
      setPreviewContent(normalizeRichTextForEditor(version.content))
    } catch {
      toast.error('Could not load this version.')
    }
  }, [sessionId, sectionId])

  const handleRestore = useCallback(async () => {
    if (!selectedId || previewContent === null) return
    setIsRestoring(true)
    try {
      const result = await restoreSegmentVersion(sessionId, sectionId, selectedId)
      onRestored({ version: result.version, content: previewContent, wordCount: result.wordCount })
      onOpenChange(false)
      toast.success('Version restored. The previous state is kept in history.')
    } catch {
      toast.error('Could not restore this version. Please try again.')
    } finally {
      setIsRestoring(false)
    }
  }, [selectedId, previewContent, sessionId, sectionId, onRestored, onOpenChange])

  const authorLabel = (v: SegmentVersionMeta) => {
    if (!v.createdBy) return 'AI'
    if (v.createdBy.id === currentUserId) return 'You'
    return v.createdBy.fullName ?? 'Teammate'
  }

  // Preview content is already normalised to HTML in handleSelect; sanitize it
  // the same way the drafting diff pipeline does (segmentDiff.ts) before rendering.
  const sanitizedPreview = useMemo(
    () => (previewContent === null ? null : DOMPurify.sanitize(previewContent)),
    [previewContent]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {sectionHeading ? `Version history — ${sectionHeading}` : 'Version history'}
          </DialogTitle>
          <DialogDescription>
            {sectionHeading
              ? 'Versions for the section your cursor is in. Select a version to preview it, then restore if needed.'
              : 'Your edits are grouped into sessions. Select a version to preview it, then restore if needed.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 min-h-[320px]">
          <div className="w-64 shrink-0 overflow-y-auto max-h-[50vh] flex flex-col gap-1">
            {isLoading && <span className="text-meta px-2 py-1">Loading…</span>}
            {!isLoading && versions.length === 0 && (
              <span className="text-meta px-2 py-1">No versions yet.</span>
            )}
            {versions.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => void handleSelect(v.id)}
                className={cn(
                  'text-left rounded-lg px-2 py-1.5 hover:bg-accent',
                  selectedId === v.id && 'bg-foreground/10'
                )}
              >
                <div className="text-sm">{authorLabel(v)}</div>
                <div className="text-meta">
                  <span>{SOURCE_LABELS[v.source]}</span>
                  <span> · {formatVersionTimestamp(v.createdAt, v.updatedAt)} · {v.wordCount} words</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex-1 bg-muted/40 rounded-lg p-3 overflow-y-auto max-h-[50vh]">
            {sanitizedPreview === null ? (
              <span className="text-sm text-muted-foreground">Select a version to preview it.</span>
            ) : (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
              />
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {canWrite && selectedId && previewContent !== null && (
            <Button onClick={() => void handleRestore()} disabled={isRestoring}>Restore</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
